import { getUrlParameterByName } from './index'

/**
 * The password-choice links received by email, by URL parameter name. They all carry the same
 * server-side token (`Account.forgotPasswordToken`) and are consumed by the same routes
 * (`/auth/reset-password/check|change`): only the wording and the validity period differ.
 *
 * - `password-reset-token`: "forgotten password" link (24 h);
 * - `user-token` (staff) and `student-token` (student): links of the account creation emails, which
 *   act as an activation (7 days, as long as the account has no password). Two names for the same
 *   thing, because the Sendgrid templates of both audiences are distinct.
 */
const TOKEN_PARAMETERS = [
  { name: 'password-reset-token', isWelcome: false },
  { name: 'user-token', isWelcome: true },
  { name: 'student-token', isWelcome: true }
]

/**
 * @typedef {object} PasswordResetLink
 * @property {string} token
 * @property {boolean} isWelcome first password of a brand new account: only the wording changes
 */

/**
 * Reads the password-choice link carried by the current URL. Must be called before any URL rewrite
 * (the page erases the token from the address bar once read).
 *
 * @returns {?PasswordResetLink} null when the URL carries none of these tokens
 */
export const getPasswordResetLink = () => {
  for (const { name, isWelcome } of TOKEN_PARAMETERS) {
    const token = getUrlParameterByName(name)

    if (token) {
      // `welcome=1`: explicit marker on a reset link, for the emails that set it themselves.
      return { token, isWelcome: isWelcome || getUrlParameterByName('welcome') === '1' }
    }
  }

  return null
}
